import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = { title: "", slug: "", excerpt: "", body: "", published: false };

export default function PostsManager() {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = new
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/posts/all");
      setPosts(data);
    } catch (e) {
      console.warn("[cms] posts load failed", e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ title: p.title, slug: p.slug, excerpt: p.excerpt || "", body: p.body, published: p.published });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/posts/${editing.id}`, {
          title: form.title, slug: form.slug || undefined,
          excerpt: form.excerpt, body: form.body, published: form.published,
        });
      } else {
        await api.post("/posts", { ...form, slug: form.slug || undefined });
      }
      toast.success(editing ? "Post updated" : "Post created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save post");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/posts/${p.id}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete post");
    }
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-display text-xl flex items-center gap-2">
          <FileText className="h-5 w-5" /> Blog posts
        </CardTitle>
        <Button onClick={openNew} data-testid="add-post-btn"
          className="rounded-full bg-[#0a2540] hover:bg-[#0a2540]/90">
          <Plus className="h-4 w-4 mr-1" /> New post
        </Button>
      </CardHeader>
      <CardContent>
        <Table data-testid="posts-table">
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead><TableHead>Slug</TableHead>
              <TableHead>Status</TableHead><TableHead>Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {posts.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No posts yet</TableCell></TableRow>
            )}
            {posts.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-medium">{p.title}</TableCell>
                <TableCell className="text-slate-500">/news/{p.slug}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={p.published ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}>
                    {p.published ? "Published" : "Draft"}
                  </Badge>
                </TableCell>
                <TableCell className="text-slate-500">{new Date(p.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-right">
                  <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`edit-post-${p.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(p)} data-testid={`delete-post-${p.id}`}
                    className="text-rose-600 hover:text-rose-700 ml-2">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl" aria-describedby={undefined}>
            <DialogHeader><DialogTitle>{editing ? "Edit post" : "New post"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input data-testid="post-title-input" value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Slug (optional)</Label>
                  <Input data-testid="post-slug-input" value={form.slug} placeholder="auto-generated"
                    onChange={(e) => setForm({ ...form, slug: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Excerpt</Label>
                <Input data-testid="post-excerpt-input" value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Body</Label>
                <Textarea rows={8} data-testid="post-body-input" value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.published} data-testid="post-published-checkbox"
                  onCheckedChange={(v) => setForm({ ...form, published: !!v })} />
                Published (visible on the website)
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={saving} data-testid="save-post-btn"
                className="rounded-full bg-[#0a2540] hover:bg-[#0a2540]/90">
                {saving ? "Saving…" : "Save post"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
